// ReservasFilter.tsx — Co Marca — Componente de filtros reutilizable
// Contiene toda la lógica de filtrado: búsqueda, sucursal, estado, estado pago
// Uso: importar y pasar los datos + callbacks necesarios

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Search, Building2, DollarSign, SlidersHorizontal,
    ChevronDown, X, Star
} from 'lucide-react';
import DefaultFilterSelector from '../Filter/DefaultFilterSelector';
import { useFilter } from '../../hooks/useFilter';

// ─────────────────────────────────────────────
// Tipos exportados para uso en páginas padre
// ─────────────────────────────────────────────
export interface ReservasFilterState {
    searchTerm: string;
    sucursalFilter: number | null;
    estadoFilter: string | null;
    estadoPagoFilter: string | null;
}

export interface ReservasFilterOptions {
    sucursales: [number, string][];
    estados: [string, string][];
    estadosPago: [string, string][];
}

export interface ReservasFilterProps {
    /** Estado actual del filtro (controlado desde el padre) */
    filterState: ReservasFilterState;
    /** Opciones para los selects */
    options: ReservasFilterOptions;
    /** Callbacks para cambiar estado */
    onSearchChange: (value: string) => void;
    onSucursalChange: (value: string) => void;
    onEstadoChange: (value: string) => void;
    onEstadoPagoChange: (value: string) => void;
    onClearAll: () => void;
    /** Total de resultados filtrados (para mostrar contador) */
    totalFiltered: number;
    totalAll: number;
    /** Hook useFilter ya inicializado en el padre */
    filterHook: ReturnType<typeof useFilter>;
}

// ─────────────────────────────────────────────
// Sub-componentes internos
// ─────────────────────────────────────────────

const FilterChip = ({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) => (
    <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 10px', borderRadius:'8px', fontSize:'.78rem', background:'var(--ad-surface, #fff)', border:'1.5px solid rgba(97, 37, 123,.2)', transition:'border-color .2s' }}>
        <span style={{ color:'var(--ad-muted, #64748b)', fontWeight:'600' }}>{label}:</span>
        <span style={{ color:'var(--ad-secondary, #61257b)', fontWeight:'700' }}>{value}</span>
        <button onClick={onRemove} style={{ marginLeft:'2px', padding:'2px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', borderRadius:'4px' }}>
            <X size={12} color="var(--ad-muted, #94a3b8)"/>
        </button>
    </div>
);

const CoSelect = ({
    value, onChange, options, placeholder, icon: Icon, focusColor = 'var(--ad-secondary, #61257b)'
}: {
    value: string; onChange: (v: string) => void;
    options: [string|number, string][]; placeholder: string;
    icon?: React.ElementType; focusColor?: string;
}) => (
    <div style={{ position:'relative' }}>
        {Icon && (
            <div style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                <Icon size={14} color="var(--ad-muted, #94a3b8)"/>
            </div>
        )}
        <select
            value={value}
            onChange={e=>onChange(e.target.value)}
            style={{ width:'100%', padding:`8px ${Icon?'32px':'12px'} 8px ${Icon?'32px':'12px'}`, paddingRight:'28px', border:'1.5px solid var(--ad-line, #e5e7eb)', borderRadius:'10px', fontSize:'.83rem', color:'var(--ad-ink, #374151)', background:'var(--ad-surface, #fafafa)', outline:'none', appearance:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", transition:'border-color .2s' }}
            onFocus={e=>e.target.style.borderColor=focusColor}
            onBlur={e=>e.target.style.borderColor='var(--ad-line, #e5e7eb)'}
        >
            <option value="">{placeholder}</option>
            {options.map(([id,nombre])=><option key={id} value={id}>{nombre}</option>)}
        </select>
        <div style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
            <ChevronDown size={13} color="var(--ad-muted, #94a3b8)"/>
        </div>
    </div>
);

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────

const colors = {
    primary:     'var(--ad-primary, #c1348b)',
    primaryDark: 'var(--ad-primary-dark, #8f1f64)',
    secondary:   'var(--ad-secondary, #61257b)',
};

const css = `

.rf-wrap * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
.rf-search-input { transition:all .2s; }
.rf-search-input:focus { border-color:${colors.secondary} !important; box-shadow:0 0 0 3px rgba(97, 37, 123,.1); background:var(--ad-surface, #fff) !important; outline:none; }
.rf-filter-btn { transition:all .2s; }
.rf-filter-btn:hover { transform:translateY(-1px); }
.rf-clear-btn:hover { background:var(--ad-line, #f1f5f9) !important; }
`;

export const ReservasFilter: React.FC<ReservasFilterProps> = ({
    filterState,
    options,
    onSearchChange,
    onSucursalChange,
    onEstadoChange,
    onEstadoPagoChange,
    onClearAll,
    totalFiltered,
    totalAll,
    filterHook,
}) => {
    const {
        defaultFilter, activeFilter, loading: filtersLoading,
        loadFilters, saveFilter, deleteFilter, setAsDefaultFilter,
        applyFilter, clearFilter, dismissDefaultFilter, resetFilters, filters,
    } = filterHook;

    const [showDropdown, setShowDropdown] = useState(false);
    const [dropdownPos, setDropdownPos]   = useState({ top:0, left:0 });
    const filterBtnRef = useRef<HTMLButtonElement>(null);

    // Recalcular posición del dropdown al abrirlo y al hacer scroll/resize
    const updateDropdownPos = useCallback(() => {
        if (!filterBtnRef.current) return;
        const r = filterBtnRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 8, left: r.left });
    }, []);

    useEffect(() => {
        if (!showDropdown) return;
        updateDropdownPos();
        window.addEventListener('scroll', updateDropdownPos, true);
        window.addEventListener('resize', updateDropdownPos);
        return () => {
            window.removeEventListener('scroll', updateDropdownPos, true);
            window.removeEventListener('resize', updateDropdownPos);
        };
    }, [showDropdown, updateDropdownPos]);

    const handleToggleDropdown = useCallback(() => {
        if (!showDropdown) updateDropdownPos();
        setShowDropdown(p => !p);
    }, [showDropdown, updateDropdownPos]);

    const { searchTerm, sucursalFilter, estadoFilter, estadoPagoFilter } = filterState;

    const hasActiveFilters = !!(searchTerm || sucursalFilter !== null || estadoFilter !== null || estadoPagoFilter !== null || activeFilter !== null);
    const activeFilterCount = [sucursalFilter, estadoFilter, estadoPagoFilter].filter(Boolean).length;

    const getCurrentFilterParams = useCallback(() => {
        const p: Record<string,any> = {};
        if (sucursalFilter) p.sucursal_id = sucursalFilter;
        if (estadoFilter)   p.estado = estadoFilter;
        if (estadoPagoFilter) p.estado_pago = estadoPagoFilter;
        return p;
    }, [sucursalFilter, estadoFilter, estadoPagoFilter]);

    const handleSaveFilter = useCallback(async (name: string, params: Record<string,any>, isDefault: boolean) => {
        await saveFilter(name, params, { isDefault, applyAfterSave: true });
        await loadFilters();
    }, [saveFilter, loadFilters]);

    const handleApplyFilter = useCallback((params: Record<string,any>|null) => {
        if (params) {
            const match = filters.find(f => JSON.stringify(f.parameters) === JSON.stringify(params));
            if (match) { applyFilter(match); return; }
            onSucursalChange(params.sucursal_id?.toString() || '');
            onEstadoChange(params.estado || '');
            onEstadoPagoChange(params.estado_pago || '');
            if (activeFilter) clearFilter();
        } else {
            onClearAll();
        }
    }, [filters, applyFilter, activeFilter, clearFilter, onSucursalChange, onEstadoChange, onEstadoPagoChange, onClearAll]);

    const handleDeleteFilter = useCallback(async (id: number) => {
        if (window.confirm('¿Eliminar este filtro?')) {
            await deleteFilter(id); await loadFilters();
        }
    }, [deleteFilter, loadFilters]);

    const handleSetAsDefault = useCallback(async (id: number) => {
        await setAsDefaultFilter(id); await loadFilters();
    }, [setAsDefaultFilter, loadFilters]);

    const handleClearAll = useCallback(() => {
        onClearAll();
        if (activeFilter) clearFilter();
        dismissDefaultFilter();
        resetFilters();
        filterBtnRef.current?.blur();
    }, [onClearAll, activeFilter, clearFilter, dismissDefaultFilter, resetFilters]);

    return (
        <>
            <style>{css}</style>
            <div className="rf-wrap">
                {/* Fila principal */}
                <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'10px' }}>
                    {/* Búsqueda */}
                    <div style={{ flex:'1 1 220px', position:'relative', minWidth:'180px' }}>
                        <Search size={14} color="var(--ad-muted, #94a3b8)" style={{ position:'absolute', left:'11px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
                        <input
                            type="text"
                            className="rf-search-input"
                            placeholder="Buscar reservas..."
                            value={searchTerm}
                            onChange={e=>onSearchChange(e.target.value)}
                            style={{ width:'100%', padding:'9px 36px', border:'1.5px solid var(--ad-line, #e5e7eb)', borderRadius:'10px', fontSize:'.83rem', color:'var(--ad-ink, #374151)', background:'var(--ad-surface, #fafafa)', fontFamily:"'DM Sans',sans-serif" }}
                        />
                        {searchTerm && (
                            <button onClick={()=>onSearchChange('')} style={{ position:'absolute', right:'8px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:'2px', display:'flex', alignItems:'center' }}>
                                <X size={13} color="var(--ad-muted, #94a3b8)"/>
                            </button>
                        )}
                    </div>

                    {/* Botón filtros rápidos */}
                    <div style={{ position:'relative' }}>
                        <button
                            ref={filterBtnRef}
                            className="rf-filter-btn"
                            onClick={handleToggleDropdown}
                            style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'10px', fontSize:'.83rem', fontWeight:'700', border:'1.5px solid', cursor:'pointer', whiteSpace:'nowrap', background: activeFilterCount>0 ? colors.secondary : '#fff', color: activeFilterCount>0 ? '#fff' : 'var(--ad-ink, #374151)', borderColor: activeFilterCount>0 ? colors.secondary : 'var(--ad-line, #e2e8f0)' }}>
                            <SlidersHorizontal size={14}/>
                            <span>Filtros</span>
                            {activeFilterCount > 0 && (
                                <span style={{ background:'rgba(255,255,255,.25)', borderRadius:'5px', padding:'0 5px', fontSize:'.72rem', fontWeight:'650' }}>{activeFilterCount}</span>
                            )}
                            <ChevronDown size={13} style={{ transform:showDropdown?'rotate(180deg)':'rotate(0)', transition:'transform .2s' }}/>
                        </button>
                    </div>

                    {/* ⬇ Dropdown via portal — siempre sobre todo */}
                    {showDropdown && createPortal(
                        <>
                            {/* Backdrop invisible para cerrar */}
                            <div
                                style={{ position:'fixed', inset:0, zIndex:99990 }}
                                onClick={()=>setShowDropdown(false)}
                            />
                            {/* Panel del filtro */}
                            <div style={{
                                position:'fixed',
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                                width:'280px',
                                background:'var(--ad-surface, #fff)',
                                borderRadius:'16px',
                                boxShadow:'0 20px 60px rgba(0,0,0,.16)',
                                border:'1.5px solid var(--ad-line, #f1f5f9)',
                                zIndex:99991,
                                overflow:'hidden',
                                fontFamily:"'DM Sans',sans-serif",
                            }} className="card custom-card vx-card">
                                <div style={{ height:'3px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},#ea8406,var(--ad-secondary, #8b3fa8))` }}/>
                                <div style={{ padding:'16px', display:'flex', flexDirection:'column', gap:'12px' }}>
                                    <div>
                                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:'650', color:'var(--ad-muted, #94a3b8)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'6px' }}>Sucursal</label>
                                        <CoSelect value={sucursalFilter?.toString()||''} onChange={onSucursalChange} options={options.sucursales} placeholder="Todas las sucursales" icon={Building2}/>
                                    </div>
                                    <div>
                                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:'650', color:'var(--ad-muted, #94a3b8)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'6px' }}>Estado de reserva</label>
                                        <CoSelect value={estadoFilter||''} onChange={onEstadoChange} options={options.estados} placeholder="Todos los estados"/>
                                    </div>
                                    <div>
                                        <label style={{ display:'block', fontSize:'.68rem', fontWeight:'650', color:'var(--ad-muted, #94a3b8)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'6px' }}>Estado de pago</label>
                                        <CoSelect value={estadoPagoFilter||''} onChange={onEstadoPagoChange} options={options.estadosPago} placeholder="Todos los pagos" icon={DollarSign}/>
                                    </div>
                                    {activeFilterCount > 0 && (
                                        <button
                                            onClick={()=>{ onSucursalChange(''); onEstadoChange(''); onEstadoPagoChange(''); setShowDropdown(false); }}
                                            style={{ padding:'7px', borderRadius:'9px', border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, color:'var(--ad-link, #2854c5)', fontSize:'.78rem', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px' }}>
                                            <X size={12}/> Limpiar filtros de panel
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>,
                        document.body
                    )}

                    {/* Filtros guardados */}
                    <DefaultFilterSelector
                        modelName="cita"
                        currentDefaultId={defaultFilter?.id || null}
                        currentParams={getCurrentFilterParams()}
                        onDefaultChanged={loadFilters}
                        onSaveFilter={handleSaveFilter}
                        onApplyFilter={handleApplyFilter}
                        onDeleteFilter={handleDeleteFilter}
                        onSetAsDefault={handleSetAsDefault}
                        loading={filtersLoading}
                    />

                    {/* Limpiar todo */}
                    {hasActiveFilters && (
                        <button className="rf-clear-btn" onClick={handleClearAll} style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'9px 14px', borderRadius:'10px', fontSize:'.83rem', fontWeight:'700', background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #e2e8f0)', color:'var(--ad-ink, #374151)', cursor:'pointer', whiteSpace:'nowrap' }}>
                            <X size={13}/>
                            <span>Limpiar</span>
                        </button>
                    )}

                    {/* Contador resultados */}
                    {hasActiveFilters && (
                        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'4px' }}>
                            <span style={{ fontSize:'1rem', fontWeight:'650', color:'var(--ad-link, #2854c5)' }}>{totalFiltered}</span>
                            <span style={{ fontSize:'.75rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>de {totalAll}</span>
                        </div>
                    )}
                </div>

                {/* Chips de filtros activos */}
                {hasActiveFilters && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'12px', paddingTop:'12px', borderTop:'1.5px solid var(--ad-line, #f1f5f9)' }}>
                        {searchTerm && (
                            <FilterChip label="Búsqueda" value={searchTerm} onRemove={()=>onSearchChange('')}/>
                        )}
                        {sucursalFilter !== null && (
                            <FilterChip label="Sucursal" value={options.sucursales.find(([id])=>id===sucursalFilter)?.[1]||''} onRemove={()=>onSucursalChange('')}/>
                        )}
                        {estadoFilter !== null && (
                            <FilterChip label="Estado" value={estadoFilter} onRemove={()=>onEstadoChange('')}/>
                        )}
                        {estadoPagoFilter !== null && (
                            <FilterChip label="Estado Pago" value={estadoPagoFilter} onRemove={()=>onEstadoPagoChange('')}/>
                        )}
                        {activeFilter && (
                            <div style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'4px 10px', borderRadius:'8px', fontSize:'.78rem', background:'rgba(97, 37, 123,.08)', border:'1.5px solid rgba(97, 37, 123,.2)' }}>
                                <Star size={11} color={colors.secondary} fill={colors.secondary}/>
                                <span style={{ color:'var(--ad-muted, #64748b)', fontWeight:'600' }}>Filtro:</span>
                                <span style={{ color:'var(--ad-link, #2854c5)', fontWeight:'700' }}>{activeFilter.filter_name}</span>
                                <button onClick={handleClearAll} style={{ marginLeft:'2px', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', padding:'2px', borderRadius:'4px' }}>
                                    <X size={12} color="var(--ad-muted, #94a3b8)"/>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ReservasFilter;
